import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { t } from '../utils/translations';

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;
/** Scan billets : session prolongée (caméra / files d’attente sans clics). */
const TICKET_SCAN_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_TIME_MS = 30 * 1000;
const ACTIVITY_THROTTLE_MS = 10_000;

const TICKET_SCAN_PATH_PREFIXES = ['/dashboard-tickets'];

function isTicketScanRoute(pathname = '') {
  return TICKET_SCAN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

const InactivityTimer = () => {
  const { currentUser, logout } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const pathnameRef = useRef(location.pathname);

  pathnameRef.current = location.pathname;

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearTimers();
    warningShownRef.current = false;
    lastActivityRef.current = Date.now();

    if (!currentUser) return;

    const timeoutMs = isTicketScanRoute(pathnameRef.current)
      ? TICKET_SCAN_TIMEOUT_MS
      : DEFAULT_TIMEOUT_MS;

    warningTimeoutRef.current = setTimeout(() => {
      if (currentUser && !warningShownRef.current) {
        warningShownRef.current = true;
        toast.warning(t(language, 'inactivity.warning'), {
          duration: WARNING_TIME_MS,
          icon: '⚠️'
        });
      }
    }, Math.max(0, timeoutMs - WARNING_TIME_MS));

    timeoutRef.current = setTimeout(() => {
      if (currentUser) {
        toast.error(t(language, 'inactivity.loggedOut'), {
          duration: 5000,
          icon: '🔒'
        });
        logout();
      }
    }, timeoutMs);
  }, [clearTimers, currentUser, language, logout]);

  useEffect(() => {
    if (!currentUser) {
      clearTimers();
      warningShownRef.current = false;
      return undefined;
    }

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > ACTIVITY_THROTTLE_MS) {
        resetTimer();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') handleActivity();
    };

    resetTimer();

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'pointerdown'
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    window.addEventListener('cep:keep-alive', handleActivity);
    window.addEventListener('focus', handleActivity);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('cep:keep-alive', handleActivity);
      window.removeEventListener('focus', handleActivity);
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimers();
    };
  }, [currentUser, language, logout, resetTimer, clearTimers, location.pathname]);

  // Sur la page scan : battement de cœur tant que l’onglet est visible
  useEffect(() => {
    if (!currentUser || !isTicketScanRoute(location.pathname)) return undefined;

    const beat = () => {
      if (document.visibilityState === 'visible') {
        window.dispatchEvent(new Event('cep:keep-alive'));
      }
    };
    beat();
    const id = setInterval(beat, 60_000);
    return () => clearInterval(id);
  }, [currentUser, location.pathname]);

  return null;
};

export default InactivityTimer;
